import React from 'react';
import TreeDataController from './TreeDataController';
import { TDataNode, TTreeProps } from './types';

export type TTreeContext<
  NodeProps extends Record<string, unknown> = Record<string, unknown>,
  NodeType extends TDataNode<NodeProps> = TDataNode<NodeProps>,
> = {
  forceUpdate: number;
  name: string;
  handler: TreeDataController<NodeProps, NodeType>;
  treeProps: TTreeProps<NodeProps, NodeType>;
} & Pick<TTreeProps, 'focusGetter'>;

const TreeContext = React.createContext({} as TTreeContext);

export function useTreeContext<
  NodeProps extends Record<string, unknown> = Record<string, unknown>,
  NodeType extends TDataNode<NodeProps> = TDataNode<NodeProps>,
>() {
  const context = React.useContext(TreeContext);
  if (!context) throw new Error('There is no tree context');
  return context as unknown as TTreeContext<NodeProps, NodeType>;
}

export const TreeContextProvider = <
  NodeProps extends Record<string, unknown> = Record<string, unknown>,
  NodeType extends TDataNode<NodeProps> = TDataNode<NodeProps>,
>({
  children,
  value,
}: {
  children: React.ReactNode;
  value: TTreeContext<NodeProps, NodeType>;
}) => {
  const Provider = React.useMemo(() => {
    return TreeContext.Provider as unknown as React.Provider<
      TTreeContext<NodeProps, NodeType>
    >;
  }, []);

  return <Provider value={value}>{children}</Provider>;
};

export default TreeContext;
